import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Store, Truck, Clock, AlertCircle } from 'lucide-react';
import useDebouncedSave from '../hooks/useDebouncedSave';
import SaveStatusPill from './SaveStatusPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PICKUP_PRESETS = [10, 15, 20, 30, 45, 60];
const DELIVERY_PRESETS = [20, 30, 45, 60, 75, 90];

const DEFAULTS = {
  // iter140 — Fast by NoFee is no longer an opt-in program (owner spec).
  // Every restaurant automatically participates by configuring their
  // Pickup / Delivery times below, so `fast_enabled` is always true.
  // The UI opt-in block was removed from OrderFulfillmentForm; the
  // field is kept in the form state and payload so the backend
  // contract (`fast_enabled: bool`) is unchanged.
  fast_enabled: true,
  pickup_enabled: true,
  pickup_avg_minutes: 20,
  pickup_available_until: '21:30',
  delivery_enabled: true,
  delivery_avg_minutes: 45,
  delivery_available_until: '21:00',
};

// Hydrate from a restaurant doc, falling back to legacy accepts_* + defaults.
const fromRestaurant = (r) => {
  if (!r) return { ...DEFAULTS };
  return {
    // iter140 — force-true. Previously read from `r.fast_enabled`
    // which could be `false` for restaurants that opted-out in older
    // onboardings. Auto-enrolling everyone matches the new spec:
    // Fast by NoFee runs automatically on top of Pickup / Delivery
    // times; no restaurant decision is required anymore.
    fast_enabled: true,
    pickup_enabled: r.pickup_enabled ?? r.accepts_pickup ?? DEFAULTS.pickup_enabled,
    pickup_avg_minutes: r.pickup_avg_minutes ?? DEFAULTS.pickup_avg_minutes,
    pickup_available_until: r.pickup_available_until || DEFAULTS.pickup_available_until,
    delivery_enabled: r.delivery_enabled ?? r.accepts_delivery ?? DEFAULTS.delivery_enabled,
    delivery_avg_minutes: r.delivery_avg_minutes ?? DEFAULTS.delivery_avg_minutes,
    delivery_available_until: r.delivery_available_until || DEFAULTS.delivery_available_until,
  };
};

/**
 * OrderFulfillmentForm — reusable settings card that lets the restaurant
 * owner control Pickup and Delivery operating settings. Mounts inside
 * Dashboard → Settings and (later) the Onboarding wizard. Debounced
 * auto-save: every change PATCH-es `PUT /api/restaurants/{id}` 700 ms
 * after the user stops interacting.
 *
 * Props:
 *   restaurantId — owner's restaurant uuid (required)
 *   token        — restaurant-owner JWT (required)
 *   onChange?    — optional `(form) => void` called whenever the form
 *                  state updates (used by the onboarding shell to refresh
 *                  the readiness banner in real time).
 */
const OrderFulfillmentForm = ({ restaurantId, token, onChange }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(DEFAULTS);
  const [loaded, setLoaded] = useState(false);

  // ── Fetch current values ──────────────────────────────────────────
  useEffect(() => {
    if (!restaurantId || !token) return;
    let cancelled = false;
    axios
      .get(`${API}/restaurants/${restaurantId}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (cancelled) return;
        // The /restaurants/{id} endpoint returns
        //   { restaurant: { ... }, categories: [], items: [] }
        // so we need to unwrap before mapping. Fall back to res.data for
        // safety in case the shape ever changes.
        const r = res.data?.restaurant || res.data;
        setForm(fromRestaurant(r));
        setLoaded(true);
      })
      .catch(() => {
        if (cancelled) return;
        // Still show the form with defaults so the owner can save.
        setLoaded(true);
      });
    return () => { cancelled = true; };
  }, [restaurantId, token]);

  // ── Debounced auto-save (PUT /api/restaurants/{id}) ───────────────
  const { status, error } = useDebouncedSave(
    form,
    async (v) => {
      await axios.put(`${API}/restaurants/${restaurantId}`, v, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    { delay: 700, enabled: loaded, skipNext: 1 }
  );

  useEffect(() => {
    if (loaded) onChange?.(form);
  }, [form, loaded, onChange]);

  const bothDisabled = !form.pickup_enabled && !form.delivery_enabled;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="order-fulfillment-form" className="text-zinc-200">
      <div className="flex items-start justify-between gap-4 mb-1">
        <div>
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-gold-400" />
            {t('fulfillment.title', 'Order Fulfillment Settings')}
          </h3>
          <p className="text-sm text-zinc-400 mt-0.5">
            {t(
              'fulfillment.subtitle',
              'Control how Pickup and Delivery work for your restaurant. These settings drive what customers see during checkout.'
            )}
          </p>
        </div>
        <SaveStatusPill status={status} error={error} />
      </div>

      {bothDisabled && (
        <div
          data-testid="fulfillment-both-disabled-warning"
          className="mt-4 flex items-start gap-2 rounded-xl border border-rose-500/40 bg-rose-500/10 text-rose-200 px-3 py-2 text-sm"
        >
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            {t(
              'fulfillment.both_disabled',
              'Please enable Pickup or Delivery before publishing your restaurant.'
            )}
          </span>
        </div>
      )}

      {/* ─── Fast by NoFee enrollment block removed in iter140 ───────
          Owner spec: Fast by NoFee is no longer an optional program.
          Every restaurant automatically participates by configuring
          its Pickup and Delivery times below. After each completed
          order the customer receives the existing confirmation email
          and confirms whether the order arrived within the promised
          time — the system records the delivery performance
          automatically. No restaurant decision is required, so the
          "Do you want to participate in Fast by NoFee?" opt-in UI
          (title + explainer + Yes/No pills + helper + program
          explainer card) was deleted here. The `fast_enabled` field
          is still hydrated as `true` and persisted through the save
          payload so the backend contract is unchanged. */}

      <div className="grid gap-4 mt-5 lg:grid-cols-2">
        {/* ─── Pickup ──────────────────────────────────────────────── */}
        <FulfillmentCard
          icon={<Store className="w-4 h-4" />}
          label={t('fulfillment.pickup', 'Pickup')}
          enabled={form.pickup_enabled}
          onToggle={(v) => setField('pickup_enabled', v)}
          presets={PICKUP_PRESETS}
          minutes={form.pickup_avg_minutes}
          onMinutes={(v) => setField('pickup_avg_minutes', v)}
          until={form.pickup_available_until}
          onUntil={(v) => setField('pickup_available_until', v)}
          minutesLabel={t('fulfillment.avg_pickup_time', 'Average Pickup Time')}
          untilLabel={t('fulfillment.pickup_until', 'Pickup Available Until')}
          testidPrefix="pickup"
          t={t}
        />

        {/* ─── Delivery ────────────────────────────────────────────── */}
        <FulfillmentCard
          icon={<Truck className="w-4 h-4" />}
          label={t('fulfillment.delivery', 'Delivery')}
          enabled={form.delivery_enabled}
          onToggle={(v) => setField('delivery_enabled', v)}
          presets={DELIVERY_PRESETS}
          minutes={form.delivery_avg_minutes}
          onMinutes={(v) => setField('delivery_avg_minutes', v)}
          until={form.delivery_available_until}
          onUntil={(v) => setField('delivery_available_until', v)}
          minutesLabel={t('fulfillment.avg_delivery_time', 'Average Delivery Time')}
          untilLabel={t('fulfillment.delivery_until', 'Delivery Available Until')}
          testidPrefix="delivery"
          t={t}
        />
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────
// FulfillmentCard — single-method card (Pickup OR Delivery).
// Defined OUTSIDE OrderFulfillmentForm so it is NOT re-created on every
// parent render — that prevents input remounts mid-keystroke (a known
// flicker bug pattern in this codebase, see OrderChannelsForm history).
// ──────────────────────────────────────────────────────────────────────
const FulfillmentCard = ({
  icon, label, enabled, onToggle,
  presets, minutes, onMinutes,
  until, onUntil,
  minutesLabel, untilLabel,
  testidPrefix, t,
}) => {
  const isCustom = !presets.includes(Number(minutes));
  return (
    <div
      data-testid={`fulfillment-${testidPrefix}-card`}
      className={`rounded-2xl border p-4 transition-colors ${
        enabled
          ? 'bg-zinc-900/60 border-zinc-700'
          : 'bg-zinc-950 border-zinc-800 opacity-70'
      }`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-white font-semibold">
          <span className="inline-flex w-6 h-6 rounded-full bg-zinc-800 items-center justify-center">
            {icon}
          </span>
          {label}
        </div>
        <button
          type="button"
          onClick={() => onToggle(!enabled)}
          data-testid={`fulfillment-${testidPrefix}-toggle`}
          aria-pressed={enabled}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
            enabled ? 'bg-gold-500' : 'bg-zinc-700'
          }`}
        >
          <span
            className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
              enabled ? 'translate-x-5' : 'translate-x-1'
            }`}
          />
        </button>
      </div>

      <div className={enabled ? '' : 'pointer-events-none'}>
        {/* Avg time */}
        <label className="block text-xs font-semibold text-zinc-300 mt-4 mb-2">
          {minutesLabel}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {presets.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => onMinutes(p)}
              data-testid={`fulfillment-${testidPrefix}-preset-${p}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
                Number(minutes) === p && !isCustom
                  ? 'bg-gold-500 text-zinc-950 border-gold-500'
                  : 'bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-zinc-500'
              }`}
            >
              {p} {t('fulfillment.min', 'min')}
            </button>
          ))}
          <div
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold border ${
              isCustom
                ? 'bg-gold-500/15 text-gold-300 border-gold-500/40'
                : 'bg-zinc-900 text-zinc-300 border-zinc-700'
            }`}
          >
            <input
              type="number"
              min={1}
              max={240}
              value={minutes ?? ''}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) onMinutes(Math.max(1, Math.min(240, v)));
              }}
              data-testid={`fulfillment-${testidPrefix}-custom`}
              className="w-12 bg-transparent outline-none text-center [&::-webkit-inner-spin-button]:appearance-none"
              aria-label={t('fulfillment.custom_minutes', 'Custom minutes')}
            />
            <span>{t('fulfillment.min', 'min')}</span>
          </div>
        </div>

        {/* Available until */}
        <label className="block text-xs font-semibold text-zinc-300 mt-4 mb-2">
          {untilLabel}
        </label>
        <input
          type="time"
          value={until || ''}
          onChange={(e) => onUntil(e.target.value)}
          data-testid={`fulfillment-${testidPrefix}-until`}
          className="w-full sm:w-40 bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-100 outline-none focus:border-gold-500 transition-colors"
        />
      </div>
    </div>
  );
};

export default OrderFulfillmentForm;
